import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Button, Card, Select, Spin, Typography } from 'antd';
// Hooks
import {
  useEditCbCompanyPreferences,
  useGetAudienceFilters,
  useGetCbCompanyPreferences,
  useGetSports,
} from 'hooks';
// Components
import LocationSelect from 'components/common/LocationSelect/LocationSelect';
// Types
import {
  AudienceAgeRange,
  AudienceEthnicity,
  AudienceGender,
  AudienceInterest,
  CampaignLocationInput,
  CampaignSmPlatform,
  CbCompanyBudgetRange,
  CbTalentBudgetRange,
  MarketingChannel,
} from 'api/graphql-global-types';
import { CbCompanyPreferencesFields } from 'api/cbCompanyPreferences/types/CbCompanyPreferencesFields';
// Constants
import {
  AUDIENCE_LANGUAGE_OPTIONS,
  BUDGET_RANGE_OPTIONS,
  MARKETING_CHANNEL_OPTIONS,
  PLATFORM_OPTIONS,
  TALENT_BUDGET_RANGE_OPTIONS,
} from './constants';
// UI
import { successNotification, errorNotification } from 'ui/Notification';
// Styles
import styles from './BrandPreferencesComponent.module.scss';

const { Title, Text } = Typography;

type PreferencesFormState = {
  sports: string[];
  budgetRanges: CbCompanyBudgetRange[];
  perTalentBudgetRanges: CbTalentBudgetRange[];
  marketingChannels: MarketingChannel[];
  smPlatforms: CampaignSmPlatform[];
  audienceGender: AudienceGender | null;
  audienceAgeRanges: AudienceAgeRange[];
  audienceEthnicities: AudienceEthnicity[];
  audienceInterests: AudienceInterest[];
  audienceLanguages: string[];
  audienceLocations: CampaignLocationInput[];
};

const EMPTY_STATE: PreferencesFormState = {
  sports: [],
  budgetRanges: [],
  perTalentBudgetRanges: [],
  marketingChannels: [],
  smPlatforms: [],
  audienceGender: null,
  audienceAgeRanges: [],
  audienceEthnicities: [],
  audienceInterests: [],
  audienceLanguages: [],
  audienceLocations: [],
};

const OFFERED_PLATFORMS = new Set(PLATFORM_OPTIONS.map(({ value }) => value));

const toFormState = (
  preferences?: CbCompanyPreferencesFields | null
): PreferencesFormState =>
  preferences
    ? {
        sports: preferences.sports ?? [],
        budgetRanges: preferences.budgetRanges ?? [],
        perTalentBudgetRanges: preferences.perTalentBudgetRanges ?? [],
        marketingChannels: preferences.marketingChannels ?? [],
        // Platforms no longer offered have no option to clear them, so they drop on the next save.
        smPlatforms: (preferences.smPlatforms ?? []).filter((platform) =>
          OFFERED_PLATFORMS.has(platform)
        ),
        audienceGender: preferences.audienceGender ?? null,
        audienceAgeRanges: preferences.audienceAgeRanges ?? [],
        audienceEthnicities: preferences.audienceEthnicities ?? [],
        audienceInterests: preferences.audienceInterests ?? [],
        audienceLanguages: preferences.audienceLanguages ?? [],
        audienceLocations: (preferences.audienceLocations ?? []).map(
          (location) => ({
            country: location.country,
            state: location.state,
            city: location.city,
            placeId: location.placeId,
            placeText: location.placeText,
            latitude: location.latitude,
            longitude: location.longitude,
          })
        ),
      }
    : EMPTY_STATE;

/** The API clears a group with `null`; `[]` is rejected. */
const orNull = <TValue,>(values: TValue[]): TValue[] | null =>
  values.length ? values : null;

/**
 * Lets an admin keep a brand's standing campaign preferences up to date -
 * the same set the brand edits under Account Settings in the campaign builder.
 */
const BrandPreferencesComponent: React.FC = () => {
  const { ownerId } = useParams<{ ownerId: string }>();

  const [formState, setFormState] = useState<PreferencesFormState>(EMPTY_STATE);

  const { data, loading, error, refetch } = useGetCbCompanyPreferences({
    ownerId,
  });
  const { data: sportsData } = useGetSports();
  const { data: audienceFiltersData } = useGetAudienceFilters();
  const [editCbCompanyPreferences, { loading: saving }] =
    useEditCbCompanyPreferences();

  const stored = data?.getCbCompanyPreferences;

  useEffect(() => {
    setFormState(toFormState(stored));
  }, [stored]);

  const audienceFilters = audienceFiltersData?.getAudienceFilters;

  const update = <TKey extends keyof PreferencesFormState>(
    key: TKey,
    value: PreferencesFormState[TKey]
  ): void => setFormState((current) => ({ ...current, [key]: value }));

  const handleSave = async (): Promise<void> => {
    if (!ownerId) {
      return;
    }

    try {
      await editCbCompanyPreferences({
        variables: {
          input: {
            ownerId,
            sports: orNull(formState.sports),
            budgetRanges: orNull(formState.budgetRanges),
            perTalentBudgetRanges: orNull(formState.perTalentBudgetRanges),
            marketingChannels: orNull(formState.marketingChannels),
            smPlatforms: orNull(formState.smPlatforms),
            audience: {
              gender: formState.audienceGender,
              ageRanges: orNull(formState.audienceAgeRanges),
              ethnicities: orNull(formState.audienceEthnicities),
              interests: orNull(formState.audienceInterests),
              languages: orNull(formState.audienceLanguages),
              locations: orNull(formState.audienceLocations),
            },
          },
        },
      });

      successNotification('Preferences updated successfully!');
      refetch();
    } catch (err: any) {
      errorNotification(err?.message || 'Failed to update preferences');
    }
  };

  if (loading && !stored) {
    return (
      <div className={styles.loadingContainer}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        message="Failed to load preferences"
        description={error.message}
      />
    );
  }

  return (
    <div className={styles.root}>
      <Card className={styles.card}>
        <Title level={4}>Preferences</Title>
        <Text type="secondary">
          What this brand typically buys and who it wants to reach. The brand
          edits the same set under Account Settings.
        </Text>

        <div className={styles.section}>
          <div className={styles.formItem}>
            <Text className={styles.formItemLabel}>Sports</Text>
            <Select
              id="sports"
              mode="multiple"
              allowClear
              className={styles.select}
              placeholder="Select sports"
              value={formState.sports}
              onChange={(value: string[]) => update('sports', value)}
              options={(sportsData?.sports ?? []).map((sport) => ({
                value: sport.name,
                label: sport.name,
              }))}
              optionFilterProp="label"
            />
          </div>

          <div className={styles.formItem}>
            <Text className={styles.formItemLabel}>Campaign Budget</Text>
            <Select
              id="budgetRanges"
              mode="multiple"
              allowClear
              className={styles.select}
              placeholder="Select typical campaign budgets"
              value={formState.budgetRanges}
              onChange={(value: CbCompanyBudgetRange[]) =>
                update('budgetRanges', value)
              }
              options={BUDGET_RANGE_OPTIONS}
              optionFilterProp="label"
            />
          </div>

          <div className={styles.formItem}>
            <Text className={styles.formItemLabel}>Per Talent Budget</Text>
            <Select
              id="perTalentBudgetRanges"
              mode="multiple"
              allowClear
              className={styles.select}
              placeholder="Select typical per talent fees"
              value={formState.perTalentBudgetRanges}
              onChange={(value: CbTalentBudgetRange[]) =>
                update('perTalentBudgetRanges', value)
              }
              options={TALENT_BUDGET_RANGE_OPTIONS}
              optionFilterProp="label"
            />
          </div>

          <div className={styles.formItem}>
            <Text className={styles.formItemLabel}>Marketing Mix</Text>
            <Select
              id="marketingChannels"
              mode="multiple"
              allowClear
              className={styles.select}
              placeholder="Select campaign types"
              value={formState.marketingChannels}
              onChange={(value: MarketingChannel[]) =>
                update('marketingChannels', value)
              }
              options={MARKETING_CHANNEL_OPTIONS}
              optionFilterProp="label"
            />
          </div>

          <div className={styles.formItem}>
            <Text className={styles.formItemLabel}>Social Platforms</Text>
            <Select
              id="smPlatforms"
              mode="multiple"
              allowClear
              className={styles.select}
              placeholder="Select platforms"
              value={formState.smPlatforms}
              onChange={(value: CampaignSmPlatform[]) =>
                update('smPlatforms', value)
              }
              options={PLATFORM_OPTIONS}
              optionFilterProp="label"
            />
          </div>
        </div>

        <div className={styles.section}>
          <Title level={5}>Audience</Title>

          <div className={styles.formItem}>
            <Text className={styles.formItemLabel}>Gender</Text>
            <Select
              id="audienceGender"
              allowClear
              className={styles.select}
              placeholder="Any gender"
              value={formState.audienceGender ?? undefined}
              onChange={(value: AudienceGender) =>
                update('audienceGender', value ?? null)
              }
              options={audienceFilters?.genders ?? []}
              optionFilterProp="label"
            />
          </div>

          <div className={styles.formItem}>
            <Text className={styles.formItemLabel}>Age Range</Text>
            <Select
              id="audienceAgeRanges"
              mode="multiple"
              allowClear
              className={styles.select}
              placeholder="Any age"
              value={formState.audienceAgeRanges}
              onChange={(value: AudienceAgeRange[]) =>
                update('audienceAgeRanges', value)
              }
              options={audienceFilters?.ageRanges ?? []}
              optionFilterProp="label"
            />
          </div>

          <div className={styles.formItem}>
            <Text className={styles.formItemLabel}>Ethnicity</Text>
            <Select
              id="audienceEthnicities"
              mode="multiple"
              allowClear
              className={styles.select}
              placeholder="Any ethnicity"
              value={formState.audienceEthnicities}
              onChange={(value: AudienceEthnicity[]) =>
                update('audienceEthnicities', value)
              }
              options={audienceFilters?.ethnicities ?? []}
              optionFilterProp="label"
            />
          </div>

          <div className={styles.formItem}>
            <Text className={styles.formItemLabel}>Interests</Text>
            <Select
              id="audienceInterests"
              mode="multiple"
              allowClear
              className={styles.select}
              placeholder="Select interests"
              value={formState.audienceInterests}
              onChange={(value: AudienceInterest[]) =>
                update('audienceInterests', value)
              }
              options={audienceFilters?.interests ?? []}
              optionFilterProp="label"
            />
          </div>

          <div className={styles.formItem}>
            <Text className={styles.formItemLabel}>Language</Text>
            <Select
              id="audienceLanguages"
              mode="multiple"
              allowClear
              className={styles.select}
              placeholder="Any language"
              value={formState.audienceLanguages}
              onChange={(value: string[]) => update('audienceLanguages', value)}
              options={AUDIENCE_LANGUAGE_OPTIONS}
              optionFilterProp="label"
            />
          </div>

          <div className={styles.formItem}>
            <Text className={styles.formItemLabel}>Target Location</Text>
            <div className={styles.select}>
              <LocationSelect
                id="audienceLocations"
                value={formState.audienceLocations}
                onChange={(locations: CampaignLocationInput[]) =>
                  update('audienceLocations', locations)
                }
                placeholder="Search country, state, or city..."
              />
            </div>
          </div>
        </div>

        <div className={styles.buttonWrapper}>
          <Button
            type="primary"
            loading={saving}
            disabled={loading}
            onClick={handleSave}
          >
            Save
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default BrandPreferencesComponent;
